import { useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  AppBar, Toolbar, Box, IconButton,
  Drawer, List, ListItem, ListItemButton, ListItemText,
  alpha,
} from '@mui/material'
import { IconMenu2, IconX, IconLeaf } from '@tabler/icons-react'
import { tokens } from '@/theme'

// News + Newsletters are unified under one News surface, backed by the
// publication API (epic e9). /publications and /newsletters redirect to /news.
const NAV_LINKS = [
  { label: 'Home',          to: '/'               },
  { label: 'For GSAs',      to: '/for-gsas'       },
  { label: 'News',          to: '/news'           },
  { label: 'GSA Resources', to: '/gsa-california' },
  { label: 'GSA Data',      to: '/gsa-data'       },
]

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { pathname } = useLocation()

  const linkSx = (active: boolean) => ({
    fontFamily: '"Poppins", system-ui, sans-serif',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: active ? tokens.green.main : tokens.textDark,
    textDecoration: 'none',
    px: 1.5, py: 0.5,
    transition: 'color 0.2s',
    '&:hover': { color: tokens.green.main },
    ...(active && { borderBottom: `2px solid ${tokens.green.main}` }),
  })

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ boxShadow: 'none' }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 4 }, minHeight: 64, gap: 2 }}>
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              textDecoration: 'none', mr: 'auto',
            }}
          >
            <IconLeaf
              size={24}
              stroke={2}
              style={{ color: tokens.green.main }}
            />
            <Box
              component="span"
              sx={{
                fontFamily: '"Poppins", system-ui, sans-serif',
                fontWeight: 900,
                fontSize: '1.35rem',
                letterSpacing: '-0.02em',
                color: tokens.green.dark,
              }}
            >
              Agro<Box component="span" sx={{ color: tokens.blue.main }}>Lense</Box>
            </Box>
          </Box>

          {/* Desktop links */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {NAV_LINKS.map(({ label, to }) => (
              <Box
                key={to}
                component={NavLink}
                to={to}
                end={to === '/'}
                sx={linkSx(pathname === to || (to !== '/' && pathname.startsWith(to)))}
              >
                {label}
              </Box>
            ))}
          </Box>

          {/* Mobile hamburger */}
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: 'none' }, color: tokens.green.dark }}
          >
            <IconMenu2 size={22} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        elevation={0}
        slotProps={{
          paper: {
            sx: { width: 260, background: tokens.cream, pt: 1, boxShadow: 'none' },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}>
          <IconButton onClick={() => setMobileOpen(false)}>
            <IconX size={20} />
          </IconButton>
        </Box>
        <List>
          {NAV_LINKS.map(({ label, to }) => (
            <ListItem key={to} disablePadding>
              <ListItemButton
                component={NavLink}
                to={to}
                onClick={() => setMobileOpen(false)}
                selected={pathname === to}
                sx={{
                  mx: 1, borderRadius: 1,
                  '&.active, &.Mui-selected': {
                    background: alpha(tokens.green.main, 0.08),
                    color: tokens.green.main,
                  },
                }}
              >
                <ListItemText
                  primary={label}
                  slotProps={{ primary: { sx: { fontWeight: 600, fontSize: '0.95rem' } } }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  )
}
